/**
 * Session context. One /api/me on shell mount; every child reads from context.
 *
 * This is what makes the SPA shell honest: the rail and top bar mount once and
 * never re-verify, because the SERVER re-verifies on every data request — a
 * revoked user's next API call 401s and the guard below routes them out. The
 * client never decides authorization; it only reacts to the server's answers.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApiError, request, type Me } from './api';

type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out'; reason: string | null }
  | { status: 'ready'; me: Me };

const SessionContext = createContext<{ me: Me; refresh: () => Promise<void> } | null>(null);

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside a signed-in shell');
  return value;
}

/** Route-level guard: children render only with a live tenant session. */
export function RequireSession({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const location = useLocation();

  async function load() {
    try {
      const me = await request<Me>('/me');
      setState({ status: 'ready', me });
    } catch (error) {
      // 403 carries a reason retrying will not fix (invite_pending,
      // company_suspended); 401 is the ordinary session-expired.
      const reason =
        error instanceof ApiError
          ? error.status === 403
            ? error.code
            : 'session_expired'
          : 'server_error';
      setState({ status: 'signed-out', reason });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === 'loading') {
    // The shell chrome around this already painted — this gates only content,
    // the same contract as the static portal's opacity gate.
    return null;
  }

  if (state.status === 'signed-out') {
    return (
      <Navigate
        to={`/login${state.reason ? `?reason=${encodeURIComponent(state.reason)}` : ''}`}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return (
    <SessionContext.Provider value={{ me: state.me, refresh: load }}>
      {children ?? <Outlet />}
    </SessionContext.Provider>
  );
}
